import type { AxiosRequestConfig } from 'axios';
import { api, apiClient } from '@/lib/api';
import type {
  BulkStoreSecretEntry,
  BulkUpdateStoresResult,
  OrderLookupResult,
  RecentWebhookEntry,
  RegisterWebhookPayload,
  ShopifyStoreAdmin,
  ShopifyStore,
  ShopifySyncResponse,
  ShopifyWebhookRecord,
  UpdateStorePayload,
  UploadedAttachment,
  WebhookStoreSummary
} from '@/types/api';

// Custom request config flag honored by the axios response interceptor in
// `@/lib/api`. When set, a 401 response will neither trigger token refresh
// nor clear the user session — required for endpoints whose 401 means the
// upstream service rejected us, not the user.
const SKIP_AUTH_REFRESH = { _skipAuthRefresh: true } as unknown as AxiosRequestConfig;

export const shopifyApi = {
  listStores: () => api.get<ShopifyStoreAdmin[]>('/shopify/stores'),
  getStore: (code: string) =>
    api.get<ShopifyStoreAdmin>(`/shopify/stores/${encodeURIComponent(code)}`),
  updateStore: (code: string, payload: UpdateStorePayload) =>
    api.patch<ShopifyStoreAdmin>(`/shopify/stores/${encodeURIComponent(code)}`, payload),
  syncOrder: (storeCode: string, orderNumber: string) =>
    api.post<ShopifySyncResponse>(
      `/shopify/orders/${encodeURIComponent(storeCode)}/${encodeURIComponent(orderNumber)}/sync`
    ),
  lookupOrder: (storeCode: string, orderNumber: string) =>
    api.get<OrderLookupResult>(
      `/shopify/orders/${encodeURIComponent(storeCode)}/${encodeURIComponent(orderNumber)}/lookup`,
      // Order lookups must NEVER trigger a session-clear: a wrong order number
      // or invalid Shopify token is not the user's auth failing.
      SKIP_AUTH_REFRESH
    ),
  // V4.4 — Draft Order lookup for B1-B5 (Sales) tickets. Same response
  // shape as lookupOrder so the New-Ticket form's autofill code is unchanged.
  lookupDraftOrder: (storeCode: string, draftId: string) =>
    api.get<OrderLookupResult>(
      `/shopify/draft-orders/${encodeURIComponent(storeCode)}/${encodeURIComponent(draftId)}/lookup`,
      SKIP_AUTH_REFRESH
    ),
  listWebhooks: (code: string) =>
    api.get<ShopifyWebhookRecord[]>(`/shopify/stores/${encodeURIComponent(code)}/webhooks`),
  registerWebhook: (code: string, payload: RegisterWebhookPayload) =>
    api.post<ShopifyWebhookRecord>(
      `/shopify/stores/${encodeURIComponent(code)}/webhooks`,
      payload
    ),
  deleteWebhook: (code: string, id: number | string) =>
    api.delete<{ success: true; webhook_id: string }>(
      `/shopify/stores/${encodeURIComponent(code)}/webhooks/${encodeURIComponent(String(id))}`
    ),
  listRecentWebhooks: (limit = 50) =>
    api.get<RecentWebhookEntry[]>(`/shopify/webhooks/recent?limit=${limit}`),
  webhookSummary: () => api.get<WebhookStoreSummary[]>('/shopify/webhooks/summary'),
  bulkUpdate: (entries: BulkStoreSecretEntry[]) =>
    api.post<BulkUpdateStoresResult>('/shopify/stores/bulk-update', { entries }),
  // V4.5c — OAuth install. Returns Shopify authorize URL; caller opens it
  // in a new tab. After consent, Shopify redirects to /oauth/callback
  // which writes the resulting access_token into store.access_token.
  startOAuthInstall: (storeCode: string) =>
    api.get<{ authUrl: string }>(
      `/shopify/oauth/install/${encodeURIComponent(storeCode)}`
    )
};

export type { ShopifyStore, ShopifyStoreAdmin };

export const attachmentsApi = {
  upload: async (file: File): Promise<UploadedAttachment> => {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post<UploadedAttachment>('/attachments/upload', form, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },
  /**
   * Stream-download a stored attachment by its s3_key. Triggers a browser save
   * dialog with the original filename. Auth header is added by the axios
   * request interceptor so the user's session token gates access.
   */
  download: async (s3Key: string, filename: string): Promise<void> => {
    const response = await apiClient.get(`/attachments/${s3Key}`, {
      responseType: 'blob'
    });
    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Defer revoke so Safari has a chance to start the download.
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  },
  /**
   * V4.9.7 — Fetch an attachment as a Blob and return a local object URL
   * the caller can use in `<img>` / `<iframe>` / `<video>` for inline
   * preview. Same backend endpoint as download (access-checked), but the
   * blob URL is local — the browser renders without Content-Disposition
   * games.
   *
   * Caller is responsible for calling URL.revokeObjectURL when the
   * preview is closed (otherwise memory leaks across previews).
   */
  view: async (s3Key: string): Promise<{ blobUrl: string; mimeType: string }> => {
    const response = await apiClient.get(`/attachments/${s3Key}`, {
      responseType: 'blob'
    });
    const blob = response.data as Blob;
    const blobUrl = window.URL.createObjectURL(blob);
    return {
      blobUrl,
      mimeType: blob.type || 'application/octet-stream'
    };
  }
};
